import { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation, useTranslationStore } from '@stores/translationStore';
import { useSessionStore } from '@stores/sessionStore';
import { promptVerifyToOrder } from '@utils/verifyToOrder';
import { apiClient } from '@services/api';
import PageSpinner from '@components/PageSpinner';
import PageHeader from '@components/PageHeader';
import './FolioPage.scss';

const getHotelSlug = (): string => {
  const stored = localStorage.getItem('gcxp_hotel_slug');
  if (stored) return stored;
  const path = window.location.pathname.split('/').filter(Boolean);
  return path[0] || '';
};

// ───────────── Types ─────────────

interface FolioCharge {
  id: number;
  description: string;
  amount: number;
  taxAmount: number;
  totalAmount: number;
  currencyCode?: string;
  currencySymbol?: string;
  paymentStatus: string;
  sourceType: string;
  orderNumber?: string;
  authorizedByName?: string;
  deviceName?: string;
  notes?: string;
  createdAt: string;
  hasEmvOption?: boolean;
  /** On the hotel's own (PMS) bill: posted, pending, being_checked, front_desk. Absent when not connected. */
  pmsStatus?: 'posted' | 'pending' | 'being_checked' | 'front_desk';
}

interface HotelBillLine {
  businessDate: string;
  description?: string;
  revenueCode?: string;
  amount: number;
  currencyCode?: string;
  isReversal: boolean;
  origin: string;
}

/** The hotel's bill from its PMS. balanceKnown=false means not opened yet — never "nothing due". */
interface HotelBill {
  balanceKnown: boolean;
  balance?: number;
  currencyCode?: string;
  syncedAtUtc?: string;
  lines: HotelBillLine[];
}

interface FolioGroup {
  id: number;
  groupType: string;
  displayName: string;
  subtotal: number;
  sortOrder: number;
  chargeCount: number;
  childGroups: FolioGroup[];
}

interface FolioResponse {
  folioId: string | null;
  roomNumber: string;
  guestName: string;
  currencyCode: string;
  currencySymbol: string;
  totalAmount: number;
  totalPaid: number;
  balance: number;
  isSettled: boolean;
  settledAt?: string;
  isOtpDevice: boolean;
  preAuthAmount: number;
  preAuthStatus: string;
  paymentEnabled: boolean;
  /** 'hotel' = charged to the room and closed by the hotel; 'in_app' = paid here. */
  settlementMode?: 'hotel' | 'in_app' | 'both';
  /** Charges already on the PMS bill can be paid here too (the payment is recorded in the PMS). */
  payPostedInApp?: boolean;
  /** In-app: paid items need a saved card and are charged to it at purchase. */
  cardRequired?: boolean;
  /** open, due or paid. */
  status?: 'open' | 'due' | 'paid';
  settledBy?: string | null;
  hasSavedCard: boolean;
  cardLast4?: string;
  cardBrand?: string;
  groups: FolioGroup[];
  recentCharges: FolioCharge[];
  hotelBill?: HotelBill | null;
}

interface FolioEvent {
  id: number;
  eventType: string;
  description?: string;
  amount?: number;
  actorType?: string;
  actorName?: string;
  deviceName?: string;
  createdAt: string;
}

interface InitiatePaymentResponse {
  orderId: string;
  paymentUrl: string | null;
  uniqueKey: string;
  alreadyPaid: boolean;
}

interface CheckoutSettleResponse {
  success: boolean;
  paymentId: string;
  invoiceId: string;
  invoiceDownloadUrl?: string;
  totalCharged: number;
  chargesSettled: number;
  paymentUrl?: string;
  orderId?: string;
}

interface GuestEmvChargeResponse {
  success: boolean;
  paymentId: string;
  status: string;
  amount: number;
  terminalName?: string;
  error?: string;
}

// ───────────── Helpers ─────────────

const GROUP_EMOJIS: Record<string, string> = {
  room_service: '🍽️',
  fnb: '🍴',
  kitchen_station: '👨‍🍳',
  laundry: '👔',
  spa: '💆',
  reservation: '🏨',
  upsell: '⭐',
  transportation: '🚗',
  manual: '📝',
  misc: '📋',
};

const STATUS_CONFIG: Record<string, { color: string; label: string }> = {
  NotPaid: { color: 'pending', label: 'Pending' },
  Paid: { color: 'paid', label: 'Paid' },
  Voided: { color: 'voided', label: 'Voided' },
  Refunded: { color: 'refunded', label: 'Refunded' },
  PreAuthHeld: { color: 'held', label: 'Hold' },
  PreAuthCaptured: { color: 'paid', label: 'Captured' },
  PreAuthReleased: { color: 'voided', label: 'Released' },
};

// ───────────── Component ─────────────

export default function FolioPage() {
  const { t } = useTranslation();
  const { loadTranslations, language } = useTranslationStore();
  const { isDeviceTrusted } = useSessionStore();
  const [folio, setFolio] = useState<FolioResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedGroupId, setExpandedGroupId] = useState<number | null>(null);
  const [groupCharges, setGroupCharges] = useState<Record<number, FolioCharge[]>>({});
  const [groupLoading, setGroupLoading] = useState<number | null>(null);
  const [showHistory, setShowHistory] = useState(false);
  const [history, setHistory] = useState<FolioEvent[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  // Payment state
  const [payingChargeId, setPayingChargeId] = useState<number | null>(null);
  const [settleLoading, setSettleLoading] = useState(false);
  const [paymentError, setPaymentError] = useState<string | null>(null);
  const [settleResult, setSettleResult] = useState<CheckoutSettleResponse | null>(null);
  // FolioVia embedded checkout SDK
  const [paymentOrderId, setPaymentOrderId] = useState<string | null>(null);
  const [saveCardMode, setSaveCardMode] = useState(false);
  const folioviaCheckoutRef = useRef<{ destroy: () => void } | null>(null);
  // EMV payment state
  const [emvChargeId, setEmvChargeId] = useState<number | null>(null);
  const [showPaymentChoice, setShowPaymentChoice] = useState<FolioCharge | null>(null);
  const [emvWaiting, setEmvWaiting] = useState(false);
  const [emvResult, setEmvResult] = useState<GuestEmvChargeResponse | null>(null);

  const loadFolio = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const hotelSlug = getHotelSlug();
      const res = await apiClient.get<FolioResponse>(`/api/v1/${hotelSlug}/folio`);
      setFolio(res.data);
    } catch (e: unknown) {
      if ((e as { name?: string })?.name === 'AbortError') return;
      setError((e as Error)?.message || 'Failed to load folio');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    window.scrollTo(0, 0);
    loadFolio();
    const hotelSlug = getHotelSlug();
    if (hotelSlug && language) {
      loadTranslations(hotelSlug, 'folio', language);
    }
    return () => controller.abort();
  }, [language, loadFolio]);

  // Clean up FolioVia checkout SDK on unmount
  useEffect(() => {
    return () => {
      if (folioviaCheckoutRef.current) {
        try { folioviaCheckoutRef.current.destroy(); } catch (_) {}
        folioviaCheckoutRef.current = null;
      }
    };
  }, []);

  /** Mount FolioVia embedded checkout SDK inside the payment modal */
  const openFolioviaCheckout = (orderId: string) => {
    // Destroy any existing checkout instance
    if (folioviaCheckoutRef.current) {
      try { folioviaCheckoutRef.current.destroy(); } catch (_) {}
      folioviaCheckoutRef.current = null;
    }

    setPaymentOrderId(orderId);

    // Mount after React renders the container div
    setTimeout(() => {
      const folioviaFactory = (window as unknown as Record<string, unknown>).Foliovia as (() => { mountCheckout: (opts: Record<string, unknown>) => { destroy: () => void } }) | undefined;
      const folioviaInstance = folioviaFactory?.();
      if (!folioviaInstance) {
        setPaymentError('Payment SDK failed to load. Please refresh and try again.');
        setPaymentOrderId(null);
        return;
      }

      folioviaCheckoutRef.current = folioviaInstance.mountCheckout({
        container: '#foliovia-checkout',
        orderId,
        appearance: {
          colorPrimary: '#1a5f4a',
          borderRadius: '8px',
          fontFamily: 'system-ui, sans-serif',
        },
        onReady: () => {},
        onSuccess: async (result: Record<string, unknown>) => {
          if (!saveCardMode) {
            // Payment mode — confirm and mark charges as paid
            try {
              const hotelSlug = getHotelSlug();
              await apiClient.post(`/api/v1/${hotelSlug}/payment/confirm-sdk`, {
                orderId: (result?.orderId as string) || orderId,
              });
            } catch (e: unknown) {
              const err = e as { response?: { status?: number; data?: unknown }; message?: string };
              console.error('[FolioVia] confirm-sdk FAILED:', err.response?.status, err.response?.data || err.message);
              setPaymentError('Payment succeeded but failed to update folio. Please refresh.');
            }
          }
          // Both modes: close modal, refresh folio (picks up hasSavedCard)
          setPaymentOrderId(null);
          setSaveCardMode(false);
          setPayingChargeId(null);
          setGroupCharges({});
          if (folioviaCheckoutRef.current) {
            try { folioviaCheckoutRef.current.destroy(); } catch (_) {}
            folioviaCheckoutRef.current = null;
          }
          await loadFolio();
        },
        onError: (err: Record<string, unknown>) => {
          console.error('[FolioVia] onError fired:', err);
          setPaymentError((err?.message as string) || 'Payment failed. Please try again.');
        },
      });
    }, 50);
  };

  const closePaymentModal = () => {
    if (folioviaCheckoutRef.current) {
      try { folioviaCheckoutRef.current.destroy(); } catch (_) {}
      folioviaCheckoutRef.current = null;
    }
    setPaymentOrderId(null);
    setSaveCardMode(false);
    setPayingChargeId(null);
  };

  const loadGroupCharges = async (groupId: number) => {
    if (expandedGroupId === groupId) {
      setExpandedGroupId(null);
      return;
    }
    setExpandedGroupId(groupId);
    if (groupCharges[groupId]) return;

    setGroupLoading(groupId);
    try {
      const hotelSlug = getHotelSlug();
      const res = await apiClient.get<FolioCharge[]>(`/api/v1/${hotelSlug}/folio/group/${groupId}/charges`);
      setGroupCharges(prev => ({ ...prev, [groupId]: res.data }));
    } catch (e) {
      console.error('Failed to load charges:', e);
    } finally {
      setGroupLoading(null);
    }
  };

  const loadHistory = async () => {
    if (showHistory) {
      setShowHistory(false);
      return;
    }
    setShowHistory(true);
    if (history.length > 0) return;

    setHistoryLoading(true);
    try {
      const hotelSlug = getHotelSlug();
      const res = await apiClient.get<FolioEvent[]>(`/api/v1/${hotelSlug}/folio/history`);
      setHistory(res.data);
    } catch (e) {
      console.error('Failed to load history:', e);
    } finally {
      setHistoryLoading(false);
    }
  };

  // ───────────── Payment Handlers ─────────────

  /** Check if charge has EMV option — if so, show choice modal; otherwise go straight to online */
  const handlePayChargeClick = (charge: FolioCharge) => {
    // Gate the whole payment entry (online + EMV) on device trust.
    if (!isDeviceTrusted()) {
      promptVerifyToOrder(() => handlePayChargeClick(charge));
      return;
    }
    if (charge.hasEmvOption) {
      setShowPaymentChoice(charge);
    } else {
      handlePayCharge(charge.id);
    }
  };

  /** Pay a single charge via online flow — FolioVia handles saved cards via deviceId */
  const handlePayCharge = async (chargeId: number) => {
    // Browse-only device: clear the room-phone gate before paying. Retry the payment on success.
    if (!isDeviceTrusted()) {
      promptVerifyToOrder(() => handlePayCharge(chargeId));
      return;
    }
    setShowPaymentChoice(null);
    setPayingChargeId(chargeId);
    setPaymentError(null);

    const hotelSlug = getHotelSlug();

    try {
      const res = await apiClient.post<InitiatePaymentResponse>(
        `/api/v1/${hotelSlug}/payment/initiate`,
        { folioChargeId: chargeId }
      );

      // FolioVia auto-charged using saved card — no iframe needed
      if (res.data.alreadyPaid) {
        setGroupCharges({});
        await loadFolio();
        return;
      }

      if (res.data.orderId) {
        // First-time card entry — show FolioVia embedded checkout
        openFolioviaCheckout(res.data.orderId);
        return; // payingChargeId will be cleared by onSuccess handler
      } else {
        setPaymentError('Payment gateway did not return a payment page. Please contact the front desk.');
      }
    } catch (e: unknown) {
      const err = e as { response?: { data?: { error?: string | { message?: string } } }; message?: string };
      const raw = err.response?.data?.error;
      const msg = typeof raw === 'string' ? raw : (raw as { message?: string })?.message || err.message || 'Payment failed';
      setPaymentError(msg);
    } finally {
      setPayingChargeId(null);
    }
  };

  /** Pay a single charge via EMV card tap at counter */
  const handleEmvCharge = async (chargeId: number) => {
    setShowPaymentChoice(null);
    setEmvChargeId(chargeId);
    setEmvWaiting(true);
    setEmvResult(null);
    setPaymentError(null);

    const hotelSlug = getHotelSlug();

    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 330000); // 5.5 min

    try {
      const res = await apiClient.post<GuestEmvChargeResponse>(
        `/api/v1/${hotelSlug}/payment/emv-charge`,
        { folioChargeId: chargeId }
      );

      clearTimeout(timer);

      if (res.data.success) {
        setEmvResult(res.data);
        setGroupCharges({});
        await loadFolio();
      } else {
        setPaymentError(res.data.error || 'EMV payment failed');
      }
    } catch (e: unknown) {
      clearTimeout(timer);
      const err = e as { response?: { status?: number; data?: { error?: string | { message?: string } } }; message?: string };
      const status = err.response?.status;
      if (status === 408) {
        setPaymentError('Card operation timed out. Please try again.');
      } else if (status === 402) {
        setPaymentError('Payment declined. Please try again or pay online.');
      } else {
        const raw = err.response?.data?.error;
        const msg = typeof raw === 'string' ? raw : (raw as { message?: string })?.message || err.message || 'EMV payment failed';
        setPaymentError(msg);
      }
    } finally {
      setEmvWaiting(false);
      setEmvChargeId(null);
    }
  };

  /** Settle all unpaid charges — calls initiate for each, FolioVia auto-charges via deviceId if card is saved */
  const handleSettleAll = async () => {
    if (!folio || folio.balance <= 0) return;

    // Browse-only device: clear the room-phone gate before settling. Retry on success.
    if (!isDeviceTrusted()) {
      promptVerifyToOrder(() => handleSettleAll());
      return;
    }

    setSettleLoading(true);
    setPaymentError(null);
    setSettleResult(null);

    try {
      const hotelSlug = getHotelSlug();
      const res = await apiClient.post<CheckoutSettleResponse>(
        `/api/v1/${hotelSlug}/payment/checkout-settle`,
        {}
      );
      if (res.data.success) {
        setSettleResult(res.data);
        setGroupCharges({});
        await loadFolio();
      } else if (res.data.orderId) {
        // Guest has no saved card yet — show FolioVia embedded checkout
        openFolioviaCheckout(res.data.orderId);
      }
    } catch (e: unknown) {
      const err = e as { response?: { data?: { error?: string | { message?: string } } }; message?: string };
      const raw = err.response?.data?.error;
      const msg = typeof raw === 'string' ? raw : (raw as { message?: string })?.message || err.message || 'Settlement failed';
      setPaymentError(msg);
    } finally {
      setSettleLoading(false);
    }
  };

  /** Save card without charging — opens SDK in save-card mode */
  const handleSaveCard = async () => {
    setPaymentError(null);
    try {
      const hotelSlug = getHotelSlug();
      const res = await apiClient.post<{ orderId: string; paymentUrl?: string }>(
        `/api/v1/${hotelSlug}/payment/save-card-session`
      );
      if (res.data.orderId) {
        setSaveCardMode(true);
        openFolioviaCheckout(res.data.orderId);
      }
    } catch (e: unknown) {
      const err = e as { response?: { data?: { error?: string } }; message?: string };
      const msg = err.response?.data?.error || err.message || 'Failed to start card save';
      setPaymentError(msg);
    }
  };

  const fmt = (amount: number) => `${folio?.currencySymbol || '₪'}${amount.toFixed(2)}`;
  const fmtDate = (d: string) => {
    const date = new Date(d);
    return `${date.toLocaleDateString()} ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
  };

  if (loading) return <PageSpinner />;

  if (error) {
    return (
      <div className="folio-page">
        <div className="folio-error">
          <span className="folio-error__icon">⚠️</span>
          <p className="folio-error__text">{error}</p>
          <button className="folio-error__retry" onClick={loadFolio}>Try Again</button>
        </div>
      </div>
    );
  }

  if (!folio || (!folio.folioId && !folio.hotelBill)) {
    return (
      <div className="folio-page">
        <PageHeader className="folio-header" title={<>🧾 {t('folio.title') || 'My Folio'}</>} />
        <div className="folio-empty">
          <span className="folio-empty__icon">📋</span>
          <p className="folio-empty__text">{t('folio.empty') || 'No charges yet'}</p>
          <p className="folio-empty__sub">
            {t('folio.emptyHint') || 'Charges from your orders and services will appear here.'}
          </p>
        </div>
        {/* A stay that pays by saved card needs the card before its first paid order. */}
        {folio?.cardRequired && !folio.hasSavedCard && (
          <div className="folio-payment">
            <p className="folio-payment__hint">{t('folio.cardRequiredHint')}</p>
            <div className="folio-card-widget">
              <button className="folio-card-widget__add" onClick={handleSaveCard}>
                <span className="folio-card-widget__add-icon">+</span>
                <span>{t('folio.saveCard') || 'Add Payment Card'}</span>
              </button>
            </div>
            <div id="foliovia-checkout" />
          </div>
        )}
      </div>
    );
  }

  // A stay connected to the hotel's PMS is billed there: a charge already on (or on its way to) the hotel's
  // bill is paid at the hotel, so the app doesn't offer to collect it a second time.
  // Unless the hotel records app payments in the PMS too (payPostedInApp), then paying here is fine.
  const billedByHotel = !!folio.hotelBill && !folio.payPostedInApp;
  const canPayInApp = (charge: FolioCharge) =>
    charge.paymentStatus === 'NotPaid' && !folio.isSettled
    && (folio.payPostedInApp
      || !(charge.pmsStatus === 'posted' || charge.pmsStatus === 'pending' || charge.pmsStatus === 'being_checked'));
  const hasUnpaidCharges = folio.balance > 0 && !folio.isSettled && !billedByHotel;
  const pmsLabel = (status?: FolioCharge['pmsStatus']) => {
    switch (status) {
      case 'posted': return t('folio.pmsPosted') || 'On your hotel bill';
      case 'pending': return t('folio.pmsPending') || 'Adding to your hotel bill';
      case 'being_checked': return t('folio.pmsBeingChecked') || 'Being checked by the hotel';
      case 'front_desk': return t('folio.pmsFrontDesk') || 'Settled at the front desk';
      default: return null;
    }
  };
  const billMoney = (amount: number, currency?: string) =>
    currency && currency !== folio.currencyCode ? `${amount.toFixed(2)} ${currency}` : fmt(amount);

  return (
    <div className="folio-page">
      {/* Header */}
      <PageHeader
        className="folio-header"
        title={<>🧾 {t('folio.title') || 'My Folio'}</>}
        subtitle={folio.roomNumber ? `${t('folio.room') || 'Room'} ${folio.roomNumber}` : undefined}
        trailing={
          <span className="folio-header__badge">
            {t('folio.allCharges') || 'Room folio'}
          </span>
        }
      />

      {/* Summary card */}
      <div className="folio-summary">
        <div className="folio-summary__row">
          <span className="folio-summary__label">{t('folio.total') || 'Total'}</span>
          <span className="folio-summary__value">{fmt(folio.totalAmount)}</span>
        </div>
        <div className="folio-summary__row">
          <span className="folio-summary__label">{t('folio.paid') || 'Paid'}</span>
          <span className="folio-summary__value folio-summary__value--paid">{fmt(folio.totalPaid)}</span>
        </div>
        <div className="folio-summary__divider" />
        <div className="folio-summary__row folio-summary__row--balance">
          <span className="folio-summary__label">{t('folio.balance') || 'Balance Due'}</span>
          <span className={`folio-summary__value ${folio.balance > 0 ? 'folio-summary__value--due' : 'folio-summary__value--clear'}`}>
            {fmt(folio.balance)}
          </span>
        </div>
        {folio.isSettled || folio.status === 'paid' ? (
          <div className="folio-summary__settled">
            ✅ {folio.settledBy === 'pms' ? t('folio.paidAtHotel') : t('folio.settled') || 'Settled'}
            {folio.settledAt && <span> · {fmtDate(folio.settledAt)}</span>}
          </div>
        ) : folio.status === 'due' ? (
          <div className="folio-summary__settled folio-summary__settled--due">
            🕒 {t('folio.statusDue')}
          </div>
        ) : null}
      </div>

      {/* v1: in-app payment is off — charges go to the PMS and the hotel takes payment at checkout. */}
      {!folio.paymentEnabled && !folio.isSettled && folio.balance > 0 && (
        <p className="folio-hotel-bill__hint">
          {t('folio.payViaHotel') || 'Charges are added to your hotel bill. You pay at checkout with the card the hotel has on file, or at the front desk.'}
        </p>
      )}

      {/* Payment section */}
      {folio.paymentEnabled && !folio.isSettled && (
        <div className="folio-payment">
          {/* Settle all button */}
          {hasUnpaidCharges && (
            <button
              className="folio-payment__settle-btn"
              onClick={handleSettleAll}
              disabled={settleLoading}
            >
              {settleLoading ? (
                <span className="folio-payment__spinner" />
              ) : (
                <>
                  {t('folio.settleAll') || 'Pay All'} — {fmt(folio.balance)}
                </>
              )}
            </button>
          )}

          {/* Saved Card Widget */}
          <div className="folio-card-widget">
            {folio.hasSavedCard ? (
              <>
                <div className="folio-card-widget__saved">
                  <div className="folio-card-widget__icon">💳</div>
                  <div className="folio-card-widget__details">
                    <span className="folio-card-widget__brand">
                      {folio.cardBrand || 'Card'} {folio.cardLast4 ? `•••• ${folio.cardLast4}` : ''}
                    </span>
                    <span className="folio-card-widget__label">{t('folio.savedForPayments') || 'Saved for payments'}</span>
                  </div>
                  <button className="folio-card-widget__change" onClick={handleSaveCard}>
                    {t('folio.changeCard') || 'Change'}
                  </button>
                </div>
              </>
            ) : (
              <button className="folio-card-widget__add" onClick={handleSaveCard}>
                <span className="folio-card-widget__add-icon">+</span>
                <span>{t('folio.saveCard') || 'Add Payment Card'}</span>
              </button>
            )}
          </div>

          {folio.cardRequired && (
            <p className="folio-payment__hint">
              {folio.hasSavedCard ? t('folio.chargedAtPurchase') : t('folio.cardRequiredHint')}
            </p>
          )}

          {/* Hint */}
          {hasUnpaidCharges && (
            <p className="folio-payment__hint">
              {t('folio.payHint') || 'Tap "Pay" on any charge to pay by credit card.'}
            </p>
          )}

          {/* Payment error */}
          {paymentError && (
            <div className="folio-payment__error">
              <span>⚠️ {paymentError}</span>
              <button onClick={() => setPaymentError(null)}>✕</button>
            </div>
          )}

          {/* Settlement success */}
          {settleResult && (
            <div className="folio-payment__success">
              <span>✅ {t('folio.settleSuccess') || 'All charges settled!'}</span>
              {settleResult.invoiceDownloadUrl && (
                <a
                  href={settleResult.invoiceDownloadUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="folio-payment__invoice-link"
                >
                  📄 {t('folio.downloadInvoice') || 'Download Invoice'}
                </a>
              )}
            </div>
          )}
        </div>
      )}

      {/* Security deposit / Pre-auth */}
      {folio.preAuthStatus && folio.preAuthStatus !== 'None' && (
        <div className="folio-deposit">
          <div className="folio-deposit__icon">🔒</div>
          <div className="folio-deposit__info">
            <span className="folio-deposit__label">
              {t('folio.securityDeposit') || 'Security Deposit'}
            </span>
            <span className="folio-deposit__amount">{fmt(folio.preAuthAmount)}</span>
          </div>
          <span className={`folio-deposit__status folio-deposit__status--${folio.preAuthStatus.toLowerCase()}`}>
            {folio.preAuthStatus === 'Held'
              ? t('folio.depositHeld') || 'On Hold'
              : folio.preAuthStatus === 'Captured'
              ? t('folio.depositCaptured') || 'Applied to charges'
              : t('folio.depositReleased') || 'Released'}
          </span>
        </div>
      )}

      {/* Charge groups */}
      {folio.groups.length > 0 && (
        <div className="folio-groups">
          <h2 className="folio-section-title">{t('folio.charges') || 'Charges'}</h2>
          {folio.groups.map(group => (
            <div key={group.id} className="folio-group">
              <button
                className={`folio-group__header ${expandedGroupId === group.id ? 'folio-group__header--active' : ''}`}
                onClick={() => loadGroupCharges(group.id)}
              >
                <div className="folio-group__info">
                  <span className="folio-group__emoji">
                    {GROUP_EMOJIS[group.groupType] || '📋'}
                  </span>
                  <div>
                    <span className="folio-group__name">{group.displayName}</span>
                    <span className="folio-group__count">
                      {group.chargeCount} {group.chargeCount === 1 ? 'item' : 'items'}
                    </span>
                  </div>
                </div>
                <div className="folio-group__right">
                  <span className="folio-group__amount">{fmt(group.subtotal)}</span>
                  <span className={`folio-group__arrow ${expandedGroupId === group.id ? 'folio-group__arrow--open' : ''}`}>
                    ›
                  </span>
                </div>
              </button>

              {/* Charges (includes child group charges) */}
              {expandedGroupId === group.id && (
                <div className="folio-group__charges">
                  {groupLoading === group.id ? (
                    <div className="folio-group__loading">{t('common.loading') || 'Loading...'}</div>
                  ) : groupCharges[group.id] ? (
                    groupCharges[group.id].length === 0 ? (
                      <div className="folio-group__loading">{t('folio.noCharges') || 'No charges'}</div>
                    ) : (
                      groupCharges[group.id].map(charge => (
                        <div key={charge.id} className="folio-charge">
                          <div className="folio-charge__main">
                            <div className="folio-charge__info">
                              <span className="folio-charge__desc">{charge.description}</span>
                              {(charge.authorizedByName || charge.orderNumber) && (
                                <span className="folio-charge__meta-line">
                                  {charge.authorizedByName && <span className="folio-charge__guest">{charge.authorizedByName}</span>}
                                  {charge.orderNumber && <span className="folio-charge__order">#{charge.orderNumber}</span>}
                                </span>
                              )}
                              <span className="folio-charge__date">{fmtDate(charge.createdAt)}</span>
                              {pmsLabel(charge.pmsStatus) && (
                                <span className={`folio-charge__pms folio-charge__pms--${charge.pmsStatus}`}>{pmsLabel(charge.pmsStatus)}</span>
                              )}
                            </div>
                            <div className="folio-charge__amounts">
                              <span className="folio-charge__total">{(charge.currencySymbol || folio?.currencySymbol || '₪')}{charge.totalAmount.toFixed(2)}</span>
                              {charge.taxAmount > 0 && (
                                <span className="folio-charge__tax">
                                  incl. {(charge.currencySymbol || folio?.currencySymbol || '₪')}{charge.taxAmount.toFixed(2)} tax
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="folio-charge__footer">
                            <span className={`folio-charge__status folio-charge__status--${STATUS_CONFIG[charge.paymentStatus]?.color || 'pending'}`}>
                              {STATUS_CONFIG[charge.paymentStatus]?.label || charge.paymentStatus}
                            </span>
                            <div className="folio-charge__footer-right">
                              {charge.deviceName && (
                                <span className="folio-charge__device">{charge.deviceName}</span>
                              )}
                              {/* Pay button for unpaid charges */}
                              {folio.paymentEnabled && canPayInApp(charge) && (
                                <button
                                  className="folio-charge__pay-btn"
                                  onClick={(e) => { e.stopPropagation(); handlePayChargeClick(charge); }}
                                  disabled={payingChargeId === charge.id || emvChargeId === charge.id}
                                >
                                  {(payingChargeId === charge.id || emvChargeId === charge.id) ? (
                                    <span className="folio-payment__spinner folio-payment__spinner--small" />
                                  ) : (
                                    t('folio.pay') || 'Pay'
                                  )}
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      ))
                    )
                  ) : null}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Recent charges — grouped by guest/device */}
      {folio.recentCharges.length > 0 && (() => {
        const grouped = folio.recentCharges.reduce((acc, charge) => {
          const key = charge.authorizedByName || charge.deviceName || t('folio.roomCharges') || 'Room';
          if (!acc[key]) acc[key] = [];
          acc[key].push(charge);
          return acc;
        }, {} as Record<string, FolioCharge[]>);
        const entries = Object.entries(grouped);
        return (
          <div className="folio-recent">
            <h2 className="folio-section-title">{t('folio.recentCharges') || 'Recent Charges'}</h2>
            {entries.map(([guestName, charges]) => (
              <div key={guestName} className="folio-device-group">
                {entries.length > 1 && (
                  <div className="folio-device-group__header">
                    <span className="folio-device-group__name">{guestName}</span>
                    <span className="folio-device-group__count">
                      {charges.length} {charges.length === 1 ? 'charge' : 'charges'}
                    </span>
                  </div>
                )}
                {charges.map(charge => (
                  <div key={charge.id} className="folio-charge folio-charge--recent">
                    <div className="folio-charge__main">
                      <div className="folio-charge__info">
                        <span className="folio-charge__desc">{charge.description}</span>
                        <span className="folio-charge__date">{fmtDate(charge.createdAt)}</span>
                        {pmsLabel(charge.pmsStatus) && (
                          <span className={`folio-charge__pms folio-charge__pms--${charge.pmsStatus}`}>{pmsLabel(charge.pmsStatus)}</span>
                        )}
                      </div>
                      <div className="folio-charge__right-col">
                        <span className="folio-charge__total">{fmt(charge.totalAmount)}</span>
                        {folio.paymentEnabled && canPayInApp(charge) && (
                          <button
                            className="folio-charge__pay-btn"
                            onClick={() => handlePayChargeClick(charge)}
                            disabled={payingChargeId === charge.id || emvChargeId === charge.id}
                          >
                            {(payingChargeId === charge.id || emvChargeId === charge.id) ? (
                              <span className="folio-payment__spinner folio-payment__spinner--small" />
                            ) : (
                              t('folio.pay') || 'Pay'
                            )}
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ))}
          </div>
        );
      })()}

      {/* The hotel's own bill (PMS) — what's been posted at the hotel itself */}
      {folio.hotelBill && (
        <div className="folio-hotel-bill">
          <h2 className="folio-section-title">{t('folio.hotelBill') || 'Your hotel bill'}</h2>
          <div className="folio-hotel-bill__balance">
            {folio.hotelBill.balanceKnown ? (
              <>
                <span>{t('folio.hotelBillBalance') || 'Balance at the hotel'}</span>
                <span className="folio-hotel-bill__amount">{billMoney(folio.hotelBill.balance ?? 0, folio.hotelBill.currencyCode)}</span>
              </>
            ) : (
              <span className="folio-hotel-bill__unknown">
                {t('folio.hotelBillNotOpen') || "Your bill at the hotel hasn't been opened yet."}
              </span>
            )}
          </div>
          {folio.hotelBill.lines.map((line, i) => (
            <div key={i} className="folio-charge folio-charge--recent">
              <div className="folio-charge__main">
                <div className="folio-charge__info">
                  <span className="folio-charge__desc">{line.description || line.revenueCode}</span>
                  <span className="folio-charge__date">
                    {fmtDate(line.businessDate)} · {t('folio.fromTheHotel') || 'From the hotel'}
                  </span>
                </div>
                <div className="folio-charge__right-col">
                  <span className="folio-charge__total">{billMoney(line.amount, line.currencyCode)}</span>
                </div>
              </div>
            </div>
          ))}
          <p className="folio-hotel-bill__hint">
            {t('folio.hotelBillHint') || 'Your hotel bill is settled at the front desk.'}
          </p>
        </div>
      )}

      {/* EMV vs Online Payment Choice Modal */}
      {showPaymentChoice && (
        <div className="folio-payment-iframe-overlay" onClick={() => setShowPaymentChoice(null)}>
          <div className="folio-emv-choice-modal" onClick={(e) => e.stopPropagation()}>
            <h3 className="folio-emv-choice-modal__title">
              {t('folio.howToPay') || 'How would you like to pay?'}
            </h3>
            <p className="folio-emv-choice-modal__amount">
              {(showPaymentChoice.currencySymbol || folio?.currencySymbol || '₪')}{showPaymentChoice.totalAmount.toFixed(2)}
            </p>
            <p className="folio-emv-choice-modal__desc">{showPaymentChoice.description}</p>
            <div className="folio-emv-choice-modal__options">
              <button
                className="folio-emv-choice-modal__option folio-emv-choice-modal__option--emv"
                onClick={() => handleEmvCharge(showPaymentChoice.id)}
              >
                <span className="folio-emv-choice-modal__icon">💳</span>
                <span className="folio-emv-choice-modal__label">
                  {t('folio.payAtCounter') || 'Tap Card at Counter'}
                </span>
                <span className="folio-emv-choice-modal__hint">
                  {t('folio.payAtCounterHint') || 'Pay using the card terminal at the front desk'}
                </span>
              </button>
              <button
                className="folio-emv-choice-modal__option folio-emv-choice-modal__option--online"
                onClick={() => handlePayCharge(showPaymentChoice.id)}
              >
                <span className="folio-emv-choice-modal__icon">🌐</span>
                <span className="folio-emv-choice-modal__label">
                  {t('folio.payOnline') || 'Pay Online'}
                </span>
                <span className="folio-emv-choice-modal__hint">
                  {t('folio.payOnlineHint') || 'Enter card details on secure checkout page'}
                </span>
              </button>
            </div>
            <button
              className="folio-emv-choice-modal__cancel"
              onClick={() => setShowPaymentChoice(null)}
            >
              {t('common.cancel') || 'Cancel'}
            </button>
          </div>
        </div>
      )}

      {/* EMV Waiting Screen */}
      {emvWaiting && (
        <div className="folio-payment-iframe-overlay">
          <div className="folio-emv-waiting" onClick={(e) => e.stopPropagation()}>
            <div className="folio-emv-waiting__icon">💳</div>
            <div className="folio-emv-waiting__spinner" />
            <h3 className="folio-emv-waiting__title">
              {t('folio.waitingForTap') || 'Waiting for Card Tap...'}
            </h3>
            <p className="folio-emv-waiting__desc">
              {t('folio.waitingForTapDesc') || 'Please tap or insert your card at the payment terminal at the front desk.'}
            </p>
          </div>
        </div>
      )}

      {/* EMV Success */}
      {emvResult?.success && (
        <div className="folio-payment-iframe-overlay" onClick={() => setEmvResult(null)}>
          <div className="folio-emv-waiting folio-emv-waiting--success" onClick={(e) => e.stopPropagation()}>
            <div className="folio-emv-waiting__icon">✅</div>
            <h3 className="folio-emv-waiting__title">
              {t('folio.paymentSuccess') || 'Payment Successful!'}
            </h3>
            <p className="folio-emv-waiting__desc">
              {(folio?.currencySymbol || '₪')}{emvResult.amount.toFixed(2)} {t('folio.charged') || 'charged'}
              {emvResult.terminalName ? ` via ${emvResult.terminalName}` : ''}
            </p>
            <button
              className="folio-emv-choice-modal__cancel"
              onClick={() => setEmvResult(null)}
            >
              {t('common.ok') || 'OK'}
            </button>
          </div>
        </div>
      )}

      {/* FolioVia embedded checkout modal */}
      {paymentOrderId && (
        <div className="folio-payment-iframe-overlay" onClick={closePaymentModal}>
          <div className="folio-payment-iframe-modal" onClick={(e) => e.stopPropagation()}>
            <div className="folio-payment-iframe-modal__header">
              <span>🔒 {t('folio.securePayment') || 'Secure Payment'}</span>
              <button className="folio-payment-iframe-modal__close" onClick={closePaymentModal}>✕</button>
            </div>
            <div id="foliovia-checkout" className="folio-payment-iframe-modal__frame" />
          </div>
        </div>
      )}

      {/* History toggle */}
      <div className="folio-history-toggle">
        <button className="folio-history-toggle__btn" onClick={loadHistory}>
          {showHistory ? t('folio.hideHistory') || 'Hide History' : t('folio.showHistory') || 'View Activity History'}
        </button>
      </div>

      {/* History */}
      {showHistory && (
        <div className="folio-history">
          {historyLoading ? (
            <div className="folio-group__loading">Loading...</div>
          ) : history.length === 0 ? (
            <p className="folio-history__empty">No activity yet</p>
          ) : (
            history.map(ev => (
              <div key={ev.id} className="folio-event">
                <div className="folio-event__type">{ev.eventType.replace(/_/g, ' ')}</div>
                {ev.description && <div className="folio-event__desc">{ev.description}</div>}
                <div className="folio-event__meta">
                  {ev.actorName && <span>{ev.actorName}</span>}
                  {ev.amount != null && <span>{fmt(ev.amount)}</span>}
                  <span>{fmtDate(ev.createdAt)}</span>
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}
